import {CommitAttribution} from '@github-ui/commits/components/CommitAttribution'
import {CopySHA} from '@github-ui/commits/components/CopySHA'
import {DeferredCommitHeaderData} from '@github-ui/commits/components/DeferredCommitHeaderData'
import {useLoadSingleDeferredCommitData} from '@github-ui/commits/shared/useLoadDeferredCommitData'
import type {CommitExtended} from '@github-ui/commits/types/commit-types'
import {shortSha} from '@github-ui/commits/utils/short-sha'
import {commitPath, repositoryTreePath, pullRequestChangesCommitRangePath} from '@github-ui/paths'
import {canSoftNavToCodeView, isCommitsEnabled, isPullRequestsEnabled} from '@github-ui/repo-app-flags'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {FileCodeIcon, ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button, Link as LinkComponent, ButtonGroup, Heading} from '@primer/react'
import {useMemo} from 'react'
import {Link} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {GlobalCommands} from '@github-ui/ui-commands'

import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import type {CommitsSelectorCommitData} from '../page-data/payloads/file-tree'

type PullRequestCommitHeaderProps = {
  commit: CommitExtended
  pullRequest?: FilesRoutePayload['pullRequest']
  commits?: CommitsSelectorCommitData[]
  repository: FilesRoutePayload['repository']
}

export function PullRequestCommitHeader({commit, pullRequest, commits = [], repository}: PullRequestCommitHeaderProps) {
  const {helpUrl} = useAppPayload<PullRequestAppPayload>()

  // Navigation logic for prev/next commit
  const {prevCommitUrl, nextCommitUrl, canNavigate} = useMemo(() => {
    if (commits.length === 0) {
      return {prevCommitUrl: null, nextCommitUrl: null, canNavigate: false}
    }
    const currentCommitOid = commit.oid
    const currentIndex = commits.findIndex(c => c.oid === currentCommitOid)
    if (currentIndex === -1) {
      return {prevCommitUrl: null, nextCommitUrl: null, canNavigate: false}
    }
    const prevCommit = currentIndex > 0 ? commits[currentIndex - 1] : null
    const nextCommit = currentIndex < commits.length - 1 ? commits[currentIndex + 1] : null
    const basePath = {
      owner: repository.ownerLogin,
      repo: repository.name,
      number: pullRequest?.number || 0,
    }
    const prevUrl = prevCommit ? pullRequestChangesCommitRangePath({...basePath, head: prevCommit.oid}) : null
    const nextUrl = nextCommit ? pullRequestChangesCommitRangePath({...basePath, head: nextCommit.oid}) : null
    return {
      prevCommitUrl: prevUrl,
      nextCommitUrl: nextUrl,
      canNavigate: true,
    }
  }, [commits, commit, pullRequest, repository])

  const navigate = useNavigate()

  const commitOid = commit.oid
  const browseFilesPath = repositoryTreePath({repo: repository, action: 'tree', commitish: commitOid})
  // Soft-navigate to the code view only when both the pull requests and code view apps are mounted
  // in the repo app; otherwise fall back to a full-page navigation.
  const softNavToCodeView = canSoftNavToCodeView() && isPullRequestsEnabled()
  const softNavToCommits = isCommitsEnabled() && isPullRequestsEnabled()
  const deferredData = useLoadSingleDeferredCommitData(
    `${commitPath({
      owner: repository.ownerLogin,
      repo: repository.name,
      commitish: commitOid,
    })}/deferred_commit_data`,
  )

  return (
    <div className="d-flex flex-column border rounded-2 overflow-hidden tmp-mb-3">
      <div className="d-flex flex-column flex-justify-between border-bottom tmp-p-3 bgColor-inset">
        <div className="flex-1">
          <Heading as="h2">
            <VerifiedHTMLText html={commit.shortMessageMarkdown} className="ws-pre-wrap f5 wb-break-word" />
          </Heading>
          {commit.bodyMessageHtml && (
            <VerifiedHTMLText html={commit.bodyMessageHtml} className="ws-pre-wrap f6 wb-break-word text-mono mt-2" />
          )}
        </div>
        <div className="d-flex flex-self-start gap-2 width-full flex-justify-between tmp-mt-3">
          {canNavigate && (
            <div className="d-flex">
              <ButtonGroup>
                <Button
                  as={Link}
                  aria-label={prevCommitUrl ? 'Previous commit' : 'No previous commit'}
                  to={prevCommitUrl ?? ''}
                  inactive={!prevCommitUrl}
                  size="small"
                  leadingVisual={ChevronLeftIcon}
                >
                  Prev
                </Button>
                <Button
                  as={Link}
                  aria-label={nextCommitUrl ? 'Next commit' : 'No next commit'}
                  to={nextCommitUrl ?? ''}
                  inactive={!nextCommitUrl}
                  size="small"
                  trailingVisual={ChevronRightIcon}
                >
                  Next
                </Button>
              </ButtonGroup>
            </div>
          )}
          {softNavToCodeView ? (
            <Button as={Link} to={browseFilesPath} leadingVisual={FileCodeIcon} size="small">
              Browse files
            </Button>
          ) : (
            <Button as={LinkComponent} href={browseFilesPath} leadingVisual={FileCodeIcon} size="small">
              Browse files
            </Button>
          )}
        </div>
      </div>
      <div className="d-flex flex-row flex-justify-between tmp-px-3 py-2 f5">
        <CommitAttribution
          commit={commit}
          repo={repository}
          settings={{
            fontColor: 'var(--fgColor-default)',
          }}
          textVariant="muted"
        >
          <DeferredCommitHeaderData deferredData={deferredData} oid={commit.oid} repo={repository} helpUrl={helpUrl} />
        </CommitAttribution>
        <div className="d-flex flex-items-center gap-2 text-mono">
          <span className="color-fg-muted">commit</span>
          {softNavToCommits ? (
            <LinkComponent
              as={Link}
              to={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: commitOid})}
              className="Link--primary"
            >
              <code>{shortSha(commitOid)}</code>
            </LinkComponent>
          ) : (
            <LinkComponent
              href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: commitOid})}
              className="Link--primary"
            >
              <code>{shortSha(commitOid)}</code>
            </LinkComponent>
          )}
          <CopySHA sha={commitOid} direction="sw" />
        </div>
      </div>
      {canNavigate && (
        <GlobalCommands
          commands={{
            'pull-request-files-changed:go-to-previous-commit': () => prevCommitUrl && navigate(prevCommitUrl),
            'pull-request-files-changed:go-to-next-commit': () => nextCommitUrl && navigate(nextCommitUrl),
          }}
        />
      )}
    </div>
  )
}
